import { useCallback, useMemo, useState } from "react";

import { useUpdateTransactionTags } from "./useUpdateTransactionTags";

/**
 * Matches `TAG_CHAR_LIMIT` in the account page's transaction dialog, so the same tag is accepted
 * on both surfaces. It is a UI rule, not a database constraint: the column holds 200 characters,
 * and the mobile app caps nothing, so longer tags already exist and are left alone.
 */
export const TAG_MAX_LENGTH = 16;

export type TagError = "empty" | "duplicate" | "tooLong";

export interface UseTransactionTagsOptions {
  onSaveError: () => void;
}

export interface UseTransactionTagsResult {
  draft: string;
  error: TagError | null;
  isSaving: boolean;
  onDraftChange: (next: string) => void;
  addTag: () => void;
  removeTag: (tag: string) => void;
}

const validate = (tag: string, current: string[]): TagError | null => {
  if (!tag) return "empty";
  if (current.includes(tag)) return "duplicate";
  if (tag.length > TAG_MAX_LENGTH) return "tooLong";
  return null;
};

export const useTransactionTags = (
  transaction: API.Transaction,
  { onSaveError }: UseTransactionTagsOptions
): UseTransactionTagsResult => {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<TagError | null>(null);
  const [seededId, setSeededId] = useState(transaction.id);

  if (seededId !== transaction.id) {
    setSeededId(transaction.id);
    setDraft("");
    setError(null);
  }

  const { mutate, isPending } = useUpdateTransactionTags({
    onError: onSaveError,
  });

  const current = useMemo(() => transaction.tags ?? [], [transaction.tags]);

  const onDraftChange = useCallback((next: string) => {
    setDraft(next);
    setError(null);
  }, []);

  // Every write sends the whole array, and nothing invalidates on success, so two overlapping PUTs
  // can leave the server holding the older list while the cache keeps the newer one — with no
  // refetch to reconcile them, the user's tag quietly disappears on their next load. One write at a
  // time, the same guard `CategorySelect` uses for corrections.
  const addTag = useCallback(() => {
    if (isPending) return;
    const tag = draft.trim();
    const refusal = validate(tag, current);
    if (refusal) {
      setError(refusal);
      return;
    }
    setDraft("");
    mutate({ transactionId: transaction.id, tags: [...current, tag] });
  }, [draft, current, isPending, mutate, transaction.id]);

  const removeTag = useCallback(
    (tag: string) => {
      if (isPending) return;
      mutate({
        transactionId: transaction.id,
        tags: current.filter((applied) => applied !== tag),
      });
    },
    [current, isPending, mutate, transaction.id]
  );

  return {
    draft,
    error,
    isSaving: isPending,
    onDraftChange,
    addTag,
    removeTag,
  };
};
